import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ActaStandardPage from "../components/layout/ActaStandardPage";
import CommerceMetricCard from "@/components/commerce/CommerceMetricCard";
import CommercialScopeFilter from "@/components/CommercialScopeFilter";

import {
  ArrowClockwise,
  CalendarCheck,
  CaretLeft,
  CaretRight,
  Clock,
  EnvelopeSimple,
  Handshake,
  MagnifyingGlass,
  Phone,
  Plus,
  WarningCircle,
} from "@phosphor-icons/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

import {
  appointments as appointmentsApi,
  prospects as prospectsApi,
  shares as sharesApi,
} from "@/api";
import {
  MEETING_OUTCOMES,
  MeetingEditorDialog,
  MeetingOutcomeDialog,
} from "@/components/CommerceAgendaDialogs";
import CommerceAgendaFollowupDialog from "@/components/CommerceAgendaFollowupDialog";
import CommerceAgendaNewActionDialog from "@/components/CommerceAgendaNewActionDialog";
import CommerceAgendaMeetingNoShowDialog from "@/components/CommerceAgendaMeetingNoShowDialog";
import ActaConfirmDialog from "@/components/ActaConfirmDialog";
import { formatApiError } from "@/lib/api";
import {
  buildMeetingCalendarLinks,
  buildMeetingDefaultSubject,
  buildProspectMeetingAddress,
  DEFAULT_MEETING_DESCRIPTION,
} from "@/lib/meetingShareLinks";


const FOLLOWUP_CHANNEL_LABELS = {
  email: "Email",
  phone: "Téléphone",
  whatsapp: "WhatsApp",
  other: "Autre",
};


const ACTION_TYPES = {
  call: {
    label: "Appel",
    icon: Phone,
  },
  email: {
    label: "Email",
    icon: EnvelopeSimple,
  },
  meeting: {
    label: "Rendez-vous",
    icon: Handshake,
  },
  followup: {
    label: "Relance",
    icon: ArrowClockwise,
  },
  other: {
    label: "Autre",
    icon: CalendarCheck,
  },
};


// ACTA_MEETING_OUTCOMES_FRONT_V3
const CLOSED_STATUSES = new Set([
  "won",
  "lost",
]);


function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}


function startOfDay(value) {
  const date = new Date(value);

  date.setHours(
    0,
    0,
    0,
    0
  );

  return date;
}


function startOfWeek(value) {
  const date = startOfDay(value);

  const day = date.getDay();

  const distance =
    day === 0
      ? -6
      : 1 - day;

  date.setDate(
    date.getDate() + distance
  );

  return date;
}


function addDays(value, amount) {
  const date = new Date(value);

  date.setDate(
    date.getDate() + amount
  );

  return date;
}


function dateKey(value) {
  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1
  ).padStart(
    2,
    "0"
  );

  const day = String(
    date.getDate()
  ).padStart(
    2,
    "0"
  );

  return `${year}-${month}-${day}`;
}


function parseActionDate(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  return date;
}


function formatTime(value) {
  const date = parseActionDate(value);

  if (!date) {
    return "—";
  }

  return date.toLocaleTimeString(
    "fr-FR",
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  );
}


function formatFullDate(value) {
  const date = parseActionDate(value);

  if (!date) {
    return "Date inconnue";
  }

  return date.toLocaleDateString(
    "fr-FR",
    {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }
  );
}


function formatShortDate(value) {
  return new Date(
    value
  ).toLocaleDateString(
    "fr-FR",
    {
      weekday: "short",
      day: "numeric",
      month: "short",
    }
  );
}


function formatNumericDate(value) {
  return new Date(
    value
  ).toLocaleDateString(
    "fr-FR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }
  );
}


function agendaAgentMeetingKey(
  action
) {
  return (
    action?.appointmentId
    || action?.meetingId
    || action?.nextActionNoteId
    || action?.id
    || ""
  );
}


function prospectTitle(prospect) {
  const contact = (
    `${prospect?.firstName || ""} ` +
    `${prospect?.lastName || ""}`
  ).trim();

  return (
    prospect?.company ||
    contact ||
    "Prospect"
  );
}


function actionProspectId(action) {
  return (
    action?.prospectId ||
    action?.id ||
    ""
  );
}


function meetingSource(action) {
  if (
    action?.agendaSource ===
    "share_meeting"
  ) {
    return "share_meeting";
  }

  if (
    action?.agendaSource ===
    "prospect_appointment"
  ) {
    return "prospect_appointment";
  }

  return "prospect";
}


function meetingIdentifier(action) {
  const source =
    meetingSource(action);

  if (source === "share_meeting") {
    return (
      action?.meetingId ||
      ""
    );
  }

  if (
    source ===
    "prospect_appointment"
  ) {
    return (
      action?.appointmentId ||
      action?.id ||
      ""
    );
  }

  return (
    action?.nextActionNoteId ||
    ""
  );
}


function meetingReportUrl(action) {
  const prospectId =
    actionProspectId(action);

  if (!prospectId) {
    return "";
  }

  const base =
    `/app/commerce/prospects/${encodeURIComponent(
      prospectId
    )}?tab=information`;

  if (action?.meetingReportNoteId) {
    return base;
  }

  const meetingId =
    meetingIdentifier(action);

  if (!meetingId) {
    return base;
  }

  return (
    `${base}` +
    `&action=meeting-report` +
    `&meetingReportId=${encodeURIComponent(
      meetingId
    )}` +
    `&meetingReportSource=${encodeURIComponent(
      meetingSource(action)
    )}`
  );
}


function prospectSubtitle(prospect) {
  const contact = (
    `${prospect?.firstName || ""} ` +
    `${prospect?.lastName || ""}`
  ).trim();

  if (
    prospect?.company &&
    contact
  ) {
    return contact;
  }

  return (
    prospect?.machineInterest ||
    prospect?.city ||
    prospect?.ownerName ||
    "Informations non renseignées"
  );
}


function actionMeta(type) {
  return (
    ACTION_TYPES[type] ||
    ACTION_TYPES.other
  );
}


// ACTA_AGENDA_MEETING_EDIT_HELPERS_V1
function parseMeetingContent(content = "") {
  const lines = String(content).split("\n").map((line) => line.trim()).filter(Boolean);
  const readValue = (prefix) => {
    const line = lines.find((item) => item.toLocaleLowerCase("fr-FR").startsWith(prefix.toLocaleLowerCase("fr-FR")));
    return line ? line.slice(prefix.length).trim() : "";
  };
  const durationMatch = readValue("Durée :").match(/\d+/);
  const typeRaw = readValue("Type :").toLocaleLowerCase("fr-FR");
  return {
    subject: lines[0] || "",
    durationMinutes: durationMatch ? durationMatch[0] : "30",
    meetingType: typeRaw.includes("phys") ? "physical" : "online",
    location: readValue("Adresse :"),
    description: readValue("Description :") || readValue("Notes :"),
  };
}

// ACTA_COMMERCE_AGENDA_CLIENT_ACTIONS_V1
function actionIsOpen(prospect) {
  const status =
    prospect?.commercialStatus;

  const isActiveClient =
    prospect?.lifecycleStage ===
      "client" &&
    prospect?.clientStatus !==
      "inactive";

  const isClosed =
    status === "lost" ||
    (
      status === "won" &&
      !isActiveClient
    );

  return (
    Boolean(
      prospect?.nextAction
    ) &&
    Boolean(
      prospect?.nextActionAt
    ) &&
    (
      !prospect?.nextActionStatus ||
      prospect.nextActionStatus ===
        "open"
    ) &&
    !isClosed
  );
}


function actionIsMeeting(action) {
  return (
    action?.nextActionType ===
    "meeting"
  );
}


// ACTA_AGENDA_FOLLOWUP_WORKFLOW_V1
function actionIsFollowup(action) {
  return (
    action?.nextActionType ===
    "followup"
  );
}


function actionIsCompleted(action) {
  return (
    action?.nextActionStatus ===
      "completed" ||
    action?.followupStatus ===
      "completed" ||
    action?.actionStatus ===
      "completed"
  );
}


function meetingOutcomeCode(action) {
  return String(
    action?.meetingOutcomeCode ||
    ""
  ).trim();
}


function meetingOutcomeMeta(action) {
  return (
    MEETING_OUTCOMES[
      meetingOutcomeCode(action)
    ] ||
    null
  );
}


function meetingEndDate(action) {
  const start = parseActionDate(
    action?.nextActionAt
  );

  if (!start) {
    return null;
  }

  const selectedSlot =
    action?.selectedSlot || {};

  if (
    selectedSlot.date &&
    selectedSlot.end
  ) {
    const explicitEnd =
      parseActionDate(
        `${selectedSlot.date}T${selectedSlot.end}`
      );
    if (explicitEnd) {
      return explicitEnd;
    }
  }

  const parsed =
    parseMeetingContent(
      action?.nextActionNoteContent ||
      ""
    );

  const durationMinutes =
    Number(
      selectedSlot.durationMinutes ||
      action?.durationMinutes ||
      parsed.durationMinutes ||
      30
    ) || 30;

  return new Date(
    start.getTime() +
    durationMinutes * 60 * 1000
  );
}


function meetingIsPast(action) {
  const end = meetingEndDate(
    action
  );

  return Boolean(
    end &&
    end.getTime() <= Date.now()
  );
}


function meetingNeedsOutcome(action) {
  return (
    actionIsMeeting(action) &&
    meetingIsPast(action) &&
    !meetingOutcomeCode(action)
  );
}


function agendaActionIsVisible(action) {
  if (actionIsMeeting(action)) {
    return true;
  }

  if (actionIsCompleted(action)) {
    return true;
  }

  return actionIsOpen(action);
}


function actionIsOverdue(action) {
  if (actionIsMeeting(action)) {
    return meetingNeedsOutcome(
      action
    );
  }

  const date = parseActionDate(
    action?.nextActionAt
  );

  return (
    actionIsOpen(action) &&
    date &&
    date.getTime() <
      Date.now()
  );
}


function ActionCard({
  prospect,
  compact = false,
  overdue = false,
  onOpen,
}) {
  const meta = actionMeta(
    prospect.nextActionType
  );

  const Icon = meta.icon;

  const isMeeting =
    actionIsMeeting(prospect);

  const outcomeMeta =
    meetingOutcomeMeta(prospect);

  const isCompletedAction =
    !isMeeting &&
    actionIsCompleted(prospect);

  const isPriority =
    overdue &&
    !outcomeMeta;

  const borderColor =
    outcomeMeta?.borderColor ||
    (
      isPriority
        ? "#FCA5A5"
        : "var(--do-border)"
    );

  const iconClass =
    outcomeMeta?.iconClass ||
    (
      isPriority
        ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
        : isMeeting
          ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
          : "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
    );

  const baseStatusLabel =
    outcomeMeta?.label ||
    (
      isCompletedAction
        ? "Action faite"
        : meta.label
    );

  const channelLabel =
    actionIsFollowup(prospect)
      ? (
        FOLLOWUP_CHANNEL_LABELS[
          prospect?.nextActionChannel
        ] ||
        ""
      )
      : "";

  const statusLabel =
    channelLabel
      ? `${baseStatusLabel} - ${channelLabel}`
      : baseStatusLabel;

  const statusClass =
    outcomeMeta?.badgeClass ||
    "border-[var(--do-border)] bg-[var(--do-surface-soft)] text-[var(--do-muted)]";

  return (
    <Link
      to={`/app/commerce/prospects/${encodeURIComponent(
        actionProspectId(prospect)
      )}`}
      className={`block rounded-xl border bg-[var(--do-bg-solid)] transition hover:-translate-y-0.5 hover:shadow-md ${
        compact
          ? "p-2.5"
          : "p-3"
      }`}
      style={{
        borderColor,
      }}
      data-testid={`agenda-action-${prospect.id}`}
      onClick={(event) => {
        if (onOpen?.(prospect)) {
          event.preventDefault();
        }
      }}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={`flex shrink-0 items-center justify-center rounded-lg ${iconClass} ${
            compact
              ? "h-7 w-7"
              : "h-8 w-8"
          }`}
        >
          <Icon
            size={15}
            weight="bold"
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate font-heading text-xs font-black text-[var(--do-ink)]">
                {prospectTitle(
                  prospect
                )}
              </div>

              <div className="mt-0.5 truncate text-xs text-[var(--do-muted)]">
                {prospectSubtitle(
                  prospect
                )}
              </div>
            </div>

            <span
              className={`shrink-0 text-xs font-heading font-bold ${
                isPriority
                  ? "text-[var(--do-accent)]"
                  : "text-[var(--do-accent)]"
              }`}
            >
              {formatTime(
                prospect.nextActionAt
              )}
            </span>
          </div>

          <div
            className={`mt-2 line-clamp-2 text-xs ${
              isPriority
                ? "font-semibold text-[var(--do-accent)]"
                : "text-[var(--do-ink-soft)]"
            }`}
          >
            {prospect.nextAction}
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span
              className={`rounded-full border px-2 py-0.5 text-[10px] font-heading font-bold ${statusClass}`}
              data-testid={
                isMeeting
                  ? "agenda-meeting-status"
                  : undefined
              }
            >
              {statusLabel}
            </span>

            {prospect.ownerName ? (
              <span className="max-w-[100px] truncate text-xs text-[var(--do-muted)]">
                {prospect.ownerName}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </Link>
  );
}


function MobileActionRow({
  prospect,
  overdue = false,
  dense = false,
  onOpen,
}) {
  const meta = actionMeta(
    prospect.nextActionType
  );

  const isMeeting =
    actionIsMeeting(prospect);

  const outcomeMeta =
    meetingOutcomeMeta(prospect);

  const isCompletedAction =
    !isMeeting &&
    actionIsCompleted(prospect);

  const isPriority =
    overdue &&
    !outcomeMeta;

  const baseStatusLabel =
    outcomeMeta?.label ||
    (
      isCompletedAction
        ? "Action faite"
        : meta.label
    );

  const channelLabel =
    actionIsFollowup(prospect)
      ? (
        FOLLOWUP_CHANNEL_LABELS[
          prospect?.nextActionChannel
        ] ||
        ""
      )
      : "";

  const statusLabel =
    channelLabel
      ? `${baseStatusLabel} - ${channelLabel}`
      : baseStatusLabel;

  return (
    <Link
      to={`/app/commerce/prospects/${encodeURIComponent(
        actionProspectId(prospect)
      )}`}
      className="block min-w-0 border-b border-[var(--do-border)] py-2 md:py-3 last:border-b-0"
      data-testid={`agenda-mobile-action-${prospect.id}`}
      onClick={(event) => {
        if (onOpen?.(prospect)) {
          event.preventDefault();
        }
      }}
    >
      <div className="grid min-w-0 grid-cols-[3.25rem_minmax(0,1fr)] gap-3">
        <div
          className={`pt-0.5 font-heading text-xs font-black tabular-nums ${
            isPriority
              ? "text-[var(--do-accent)]"
              : "text-[var(--do-ink)]"
          }`}
        >
          {formatTime(
            prospect.nextActionAt
          )}
        </div>

        <div className="min-w-0">
          <div className="truncate font-heading text-sm font-black text-[var(--do-ink)]">
            {prospectTitle(
              prospect
            )}
          </div>

          {!dense ? (
            <div className="mt-0.5 truncate text-xs text-[var(--do-muted)]">
              {prospectSubtitle(
                prospect
              )}
            </div>
          ) : null}

          <div
            className={`mt-1 text-xs leading-5 ${
              "line-clamp-2"
            } ${
              isPriority
                ? "font-semibold text-[var(--do-accent)]"
                : "text-[var(--do-ink-soft)]"
            }`}
          >
            {prospect.nextAction}
          </div>

          <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className="rounded-full border border-[var(--do-border)] bg-[var(--do-surface-soft)] px-2 py-0.5 text-[10px] font-heading font-bold text-[var(--do-muted)]"
              data-testid={
                isMeeting
                  ? "agenda-mobile-meeting-status"
                  : undefined
              }
            >
              {statusLabel}
            </span>

            {!dense && prospect.ownerName ? (
              <span className="min-w-0 max-w-full truncate text-[11px] text-[var(--do-muted)]">
                {prospect.ownerName}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </Link>
  );
}


function MobileActionList({
  items,
  overdue = false,
  onOpen,
  testId,
  initialVisible = 3,
}) {
  const [expanded, setExpanded] =
    useState(false);

  const dense =
    items.length > initialVisible;

  const visibleItems =
    expanded
      ? items
      : items.slice(
          0,
          initialVisible
        );

  const remaining =
    Math.max(
      0,
      items.length -
        visibleItems.length
    );

  return (
    <div
      className="min-w-0"
      data-testid={testId}
    >
      <div>
        {visibleItems.map(
          (prospect) => (
            <MobileActionRow
              key={prospect.id}
              prospect={prospect}
              overdue={
                overdue ||
                actionIsOverdue(
                  prospect
                )
              }
              dense={dense}
              onOpen={onOpen}
            />
          )
        )}
      </div>

      {items.length >
        initialVisible ? (
        <button
          type="button"
          onClick={() =>
            setExpanded(
              (current) =>
                !current
            )
          }
          className="mt-1 min-h-10 w-full rounded-lg px-3 py-2 text-center text-xs font-heading font-bold text-[var(--do-accent)] transition hover:bg-[var(--do-surface-soft)]"
          aria-expanded={expanded}
          data-testid={`${testId}-toggle`}
        >
          {expanded
            ? "Réduire"
            : `Afficher les ${remaining} autre${remaining > 1 ? "s" : ""}`}
        </button>
      ) : null}
    </div>
  );
}


// ACTA_COMMERCE_AGENDA_FAST_FRONT_V1
export default function CommerceAgenda() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const agentMeetingBridgeRef =
    useRef("");

  const requestedAgentMeetingKey =
    searchParams.get(
      "agentMeetingKey"
    ) || "";

  const requestedAgentMeetingAction =
    searchParams.get(
      "agentMeetingAction"
    ) || "";

  const requestedAgentMeetingOutcome =
    searchParams.get(
      "agentMeetingOutcome"
    ) || "";
  const requestedAgentMeetingDate =
    searchParams.get(
      "agentMeetingDate"
    ) || "";

  const requestedAgentMeetingStart =
    searchParams.get(
      "agentMeetingStart"
    ) || "";

  const requestedAgentMeetingDuration =
    searchParams.get(
      "agentMeetingDurationMinutes"
    ) || "";

  const requestedAgentMeetingType =
    searchParams.get(
      "agentMeetingMeetingType"
    ) || "";

  const requestedAgentMeetingSubject =
    searchParams.get(
      "agentMeetingSubject"
    ) || "";

  const [
    prospects,
    setProspects,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    commercialFilter,
    setCommercialFilter,
  ] = useState("all");

  const [
    commercialUsers,
    setCommercialUsers,
  ] = useState([]);

  const canFilterCommercials = [
    "admin",
    "manager",
  ].includes(user?.role);

   const [
    weekStart,
    setWeekStart,
  ] = useState(
    startOfWeek(
      new Date()
    )
  );

  const [meetingEditor, setMeetingEditor] = useState(null);
  const [meetingSaving, setMeetingSaving] = useState(false);
  const [meetingContacts, setMeetingContacts] = useState([]);
  const [meetingContactsLoading, setMeetingContactsLoading] = useState(false);
  const [meetingOutcomeEditor, setMeetingOutcomeEditor] = useState(null);
  const [meetingOutcomeSaving, setMeetingOutcomeSaving] = useState(false);

  const [
    meetingNoShowEditor,
    setMeetingNoShowEditor,
  ] = useState(null);

  const [
    meetingDeleteTarget,
    setMeetingDeleteTarget,
  ] = useState(null);
  const [followupEditor, setFollowupEditor] = useState(null);
  const [followupSaving, setFollowupSaving] = useState(false);
  const [newActionOpen, setNewActionOpen] = useState(false);
  const [newActionSaving, setNewActionSaving] = useState(false);
  const [newActionInitialMode, setNewActionInitialMode] = useState("choose");
  const [newActionInitialProspectId, setNewActionInitialProspectId] = useState("");


  const loadAgenda =
    useCallback(
      async () => {
        setLoading(true);
        setError("");

        try {
          const response =
            await prospectsApi.agenda();

          setProspects(
            safeArray(response)
          );
        } catch (requestError) {
          const message =
            formatApiError(
              requestError
                ?.response
                ?.data
                ?.detail ||
              requestError?.message
            ) ||
            "Impossible de charger l’agenda commercial.";

          setError(message);
          toast.error(message);
        } finally {
          setLoading(false);
        }
      },
      []
    );


  useEffect(() => {
    loadAgenda();
  }, [loadAgenda]);


  useEffect(() => {
    if (!canFilterCommercials) {
      setCommercialUsers([]);
      setCommercialFilter("all");
      return undefined;
    }

    let cancelled = false;

    prospectsApi
      .listCommercialUsers()
      .then((rows) => {
        if (cancelled) {
          return;
        }

        setCommercialUsers(
          safeArray(rows).filter(
            (entry) =>
              entry?.id
              && [
                "admin",
                "manager",
                "user",
              ].includes(entry.role)
          )
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCommercialUsers([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canFilterCommercials]);


  const agendaCommercialOptions = useMemo(() => {
    const allLabel =
      user?.role === "manager"
        ? "Mon équipe"
        : "Tous les commerciaux";
    const currentUserId = String(
      user?.id || ""
    );

    return [
      {
        key: "all",
        value: "all",
        label: allLabel,
      },
      ...(currentUserId
        ? [
            {
              key: "self",
              value: "self",
              label: "Moi — utilisateur en cours",
            },
          ]
        : []),
      ...commercialUsers
        .filter(
          (entry) =>
            entry?.id
            && String(entry.id)
              !== currentUserId
        )
        .map((entry) => ({
          key: `owner-${entry.id}`,
          value: `owner:${entry.id}`,
          label: entry.name,
        })),
    ];
  }, [
    commercialUsers,
    user?.id,
    user?.role,
  ]);


  const weekDays = useMemo(
    () =>
      Array.from(
        {
          length: 7,
        },
        (
          _,
          index
        ) =>
          addDays(
            weekStart,
            index
          )
      ),
    [weekStart]
  );


  const openActions = useMemo(
    () =>
      prospects
        .filter(
          agendaActionIsVisible
        )
        .sort(
          (
            first,
            second
          ) =>
            new Date(
              first.nextActionAt
            ).getTime() -
            new Date(
              second.nextActionAt
            ).getTime()
        ),
    [prospects]
  );


  const commercialScopedActions =
    useMemo(
      () => {
        if (
          !canFilterCommercials
          || commercialFilter === "all"
        ) {
          return openActions;
        }

        const selectedOwnerId =
          commercialFilter === "self"
            ? String(user?.id || "")
            : commercialFilter.startsWith(
                "owner:"
              )
              ? commercialFilter.slice(6)
              : "";

        if (!selectedOwnerId) {
          return openActions;
        }

        return openActions.filter(
          (action) =>
            String(
              action?.createdBy || ""
            ) === selectedOwnerId
        );
      },
      [
        canFilterCommercials,
        commercialFilter,
        openActions,
        user?.id,
      ]
    );


  const filteredActions =
    useMemo(
      () => {
        const normalizedSearch =
          search
            .trim()
            .toLocaleLowerCase(
              "fr-FR"
            );

        return commercialScopedActions.filter(
          (prospect) => {
             if (
              !normalizedSearch
            ) {
              return true;
            }

            const haystack = [
              prospect.firstName,
              prospect.lastName,
              prospect.company,
              prospect.city,
              prospect.machineInterest,
              prospect.nextAction,
              prospect.ownerName,
              prospect.meetingOutcomeLabel,
            ]
              .filter(Boolean)
              .join(" ")
              .toLocaleLowerCase(
                "fr-FR"
              );

            return haystack.includes(
              normalizedSearch
            );
          }
        );
      },
      [
        commercialScopedActions,
        search,
      ]
    );


  const actionsByDay =
    useMemo(
      () => {
        const groups = {};

        weekDays.forEach(
          (day) => {
            groups[
              dateKey(day)
            ] = [];
          }
        );

        filteredActions.forEach(
          (prospect) => {
            const key = dateKey(
              prospect.nextActionAt
            );

            if (
              Object.prototype.hasOwnProperty.call(
                groups,
                key
              )
            ) {
              groups[key].push(
                prospect
              );
            }
          }
        );

        return groups;
      },
      [
        filteredActions,
        weekDays,
      ]
    );


  const todayKey = dateKey(
    new Date()
  );

  const overdueActions =
    filteredActions.filter(
      actionIsOverdue
    );

  const todayActions =
    filteredActions.filter(
      (prospect) =>
        dateKey(
          prospect.nextActionAt
        ) === todayKey
    );

  const weekActionCount =
    Object.values(
      actionsByDay
    ).reduce(
      (
        total,
        items
      ) =>
        total +
        items.length,
      0
    );

  const meetingCount =
    filteredActions.filter(
      (prospect) =>
        prospect.nextActionType ===
        "meeting"
    ).length;

  const futureActions =
    filteredActions
      .filter(
        (prospect) => {
          const date =
            parseActionDate(
              prospect.nextActionAt
            );

          const weekEnd =
            addDays(
              weekStart,
              7
            );

          return (
            date &&
            date.getTime() >=
              weekEnd.getTime()
          );
        }
      )
      .slice(
        0,
        5
      );


  const goToCurrentWeek = () => {
    setWeekStart(
      startOfWeek(
        new Date()
      )
    );
  };


  const updateMeetingEditor = (
    patch
  ) => {
    setMeetingEditor(
      (current) => {
        if (!current) {
          return current;
        }

        const next = {
          ...current,
          ...patch,
        };

        const selectedContact =
          meetingContacts.find(
            (contact) =>
              contact?.id ===
              (
                patch.prospectId ||
                next.prospectId
              )
          ) ||
          current.action ||
          null;

        if (
          patch.prospectId &&
          current.source ===
            "new"
        ) {
          const previousContact =
            meetingContacts.find(
              (contact) =>
                contact?.id ===
                current.prospectId
            ) || {};

          const previousDefaultSubject =
            buildMeetingDefaultSubject(
              previousContact,
              user || {}
            );

          const currentSubject =
            String(
              current.subject || ""
            ).trim();

          if (
            !currentSubject ||
            currentSubject ===
              previousDefaultSubject
          ) {
            next.subject =
              buildMeetingDefaultSubject(
                selectedContact || {},
                user || {}
              );
          }

          if (
            !String(
              current.description || ""
            ).trim()
          ) {
            next.description =
              DEFAULT_MEETING_DESCRIPTION;
          }
        }

        if (
          patch.prospectId &&
          next.meetingType ===
            "physical"
        ) {
          next.location =
            buildProspectMeetingAddress(
              selectedContact
            );
        } else if (
          patch.meetingType ===
            "physical" &&
          !next.location
        ) {
          next.location =
            buildProspectMeetingAddress(
              selectedContact
            );
        }

        return next;
      }
    );
  };


  const updateFollowupEditor = (patch) => {
    setFollowupEditor((current) =>
      current
        ? { ...current, ...patch }
        : current
    );
  };




  // ACTA_MEETING_CALENDAR_DERIVED_V2
  const calendarLinksForEditor =
    meetingEditor
      ? buildMeetingCalendarLinks(
          meetingEditor,
          meetingEditor.action ||
          meetingContacts.find(
            (contact) =>
              contact?.id ===
              meetingEditor.prospectId
          ) ||
          {}
        )
      : null;


  const calendarLinksForAction = (
    action
  ) => {
    if (
      !action ||
      meetingOutcomeCode(action) ===
        "honored"
    ) {
      return null;
    }

    if (
      action.agendaSource ===
        "share_meeting"
    ) {
      const slot =
        action.selectedSlot || {};

      return buildMeetingCalendarLinks(
        {
          date: slot.date || "",
          start: slot.start || "",
          durationMinutes:
            String(
              slot.durationMinutes || 30
            ),
          meetingType:
            slot.locationType ||
            "online",
          location:
            slot.locationType ===
              "physical"
              ? slot.location || ""
              : "",
          subject:
            action.nextAction ||
            "Rendez-vous",
          description:
            action.agendaDescription ||
            "",
        },
        action
      );
    }

    if (
      action.agendaSource ===
      "prospect_appointment"
    ) {
      return buildMeetingCalendarLinks(
        {
          date:
            dateKey(
              action.nextActionAt
            ),
          start:
            formatTime(
              action.nextActionAt
            ),
          durationMinutes:
            String(
              action.durationMinutes ||
              30
            ),
          meetingType:
            action.meetingType ||
            "online",
          location:
            action.meetingType ===
              "physical"
              ? (
                  action.location ||
                  buildProspectMeetingAddress(
                    action
                  )
                )
              : "",
          subject:
            action.appointmentSubject ||
            action.nextAction ||
            "Rendez-vous",
          description:
            action.agendaDescription ||
            "",
        },
        action
      );
    }

    const parsed =
      parseMeetingContent(
        action.nextActionNoteContent
      );

    return buildMeetingCalendarLinks(
      {
        date:
          dateKey(
            action.nextActionAt
          ),
        start:
          formatTime(
            action.nextActionAt
          ),
        durationMinutes:
          parsed.durationMinutes ||
          "30",
        meetingType:
          parsed.meetingType ||
          "online",
        location:
          parsed.location ||
          buildProspectMeetingAddress(
            action
          ),
        subject:
          parsed.subject ||
          action.nextAction ||
          "Rendez-vous",
        description:
          parsed.description ||
          "",
      },
      action
    );
  };


  // ACTA_MEETING_OUTCOME_DIRECT_WORKFLOW_V5
  const openMeetingOutcome = (
    action
  ) => {
    const existingOutcome =
      meetingOutcomeCode(action);

    setMeetingOutcomeEditor({
      action,
      existingOutcome:
        Boolean(existingOutcome),
      resultCode:
        existingOutcome,
    });
  };



  const saveMeetingOutcome = async (
    resultCode,
    {
      note = "",
      replanify = false,
    } = {}
  ) => {
    if (!meetingOutcomeEditor) {
      return false;
    }

    if (
      ![
        "honored",
        "no_show",
      ].includes(resultCode)
    ) {
      return false;
    }

    const { action } =
      meetingOutcomeEditor;

    const source =
      meetingSource(action);

    const meetingId =
      meetingIdentifier(action);

    const prospectId =
      actionProspectId(action);

    if (
      !meetingId ||
      !prospectId
    ) {
      toast.error(
        "Rendez-vous ou contact introuvable."
      );

      return false;
    }

    const cleanNote =
      String(
        note || ""
      )
        .trim()
        .slice(
          0,
          2000
        );

    setMeetingOutcomeSaving(true);

    try {
      await prospectsApi
        .recordMeetingOutcome(
          source,
          meetingId,
          {
            result_code:
              resultCode,
            note:
              cleanNote,
            next_action_type:
              null,
            next_action:
              null,
            next_action_at:
              null,
          }
        );

      await loadAgenda();

      const label =
        MEETING_OUTCOMES[
          resultCode
        ]?.label ||
        "Résultat enregistré";

      setMeetingNoShowEditor(
        null
      );

      if (replanify) {
        setMeetingOutcomeEditor(
          null
        );

        toast.success(
          label
        );

        await openNewMeeting(
          prospectId
        );

        return true;
      }

      setMeetingOutcomeEditor(
        (current) =>
          current
            ? {
                ...current,
                existingOutcome:
                  true,
                resultCode,
                action: {
                  ...current.action,
                  meetingOutcomeCode:
                    resultCode,
                  meetingOutcomeLabel:
                    label,
                  meetingOutcomeNote:
                    cleanNote,
                },
              }
            : current
      );

      toast.success(
        label
      );

      return true;
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            ?.response
            ?.data
            ?.detail
        ) ||
        "Enregistrement du résultat impossible"
      );

      return false;
    } finally {
      setMeetingOutcomeSaving(
        false
      );
    }
  };


  const openMeetingNoShow = () => {
    const action =
      meetingOutcomeEditor
        ?.action;

    if (!action) {
      return;
    }

    setMeetingNoShowEditor({
      action,
    });
  };


  const saveMeetingNoShow = ({
    note = "",
    replanify = false,
    skip = false,
  } = {}) =>
    saveMeetingOutcome(
      "no_show",
      {
        note:
          skip
            ? ""
            : note,
        replanify,
      }
    );


  const loadActionContacts = useCallback(async () => {
    setMeetingContactsLoading(true);

    try {
      const response = await prospectsApi.list();
      const contacts = safeArray(response)
        .filter((contact) => contact?.id)
        .sort((first, second) =>
          prospectTitle(first).localeCompare(prospectTitle(second), "fr-FR")
        );

      setMeetingContacts(contacts);
      return contacts;
    } catch (requestError) {
      toast.error(
        formatApiError(requestError?.response?.data?.detail) ||
          "Chargement des contacts impossible"
      );
      return [];
    } finally {
      setMeetingContactsLoading(false);
    }
  }, []);


  const openNewAction = useCallback(async ({
    mode = "choose",
    prospectId = "",
  } = {}) => {
    const contacts = meetingContacts.length
      ? meetingContacts
      : await loadActionContacts();

    if (!contacts.length) {
      toast.error("Aucun prospect ou client disponible.");
      return false;
    }

    const requestedContactExists =
      prospectId &&
      contacts.some(
        (contact) => contact.id === prospectId
      );

    setNewActionInitialMode(
      mode === "followup"
        ? "followup"
        : "choose"
    );
    setNewActionInitialProspectId(
      requestedContactExists
        ? prospectId
        : contacts[0].id
    );
    setNewActionOpen(true);
    return true;
  }, [meetingContacts, loadActionContacts]);


  useEffect(() => {
    const action = searchParams.get("action") || "";

    if (
      action !== "new-action" &&
      action !== "new-followup"
    ) {
      return;
    }

    let cancelled = false;

    const openRequestedAction = async () => {
      const opened = await openNewAction({
        mode:
          action === "new-followup"
            ? "followup"
            : "choose",
        prospectId:
          searchParams.get("prospectId") || "",
      });

      if (!opened || cancelled) {
        return;
      }

      const nextParams =
        new URLSearchParams(searchParams);
      nextParams.delete("action");
      nextParams.delete("prospectId");
      setSearchParams(
        nextParams,
        { replace: true }
      );
    };

    void openRequestedAction();

    return () => {
      cancelled = true;
    };
  }, [
    openNewAction,
    searchParams,
    setSearchParams,
  ]);

  const createFollowup = async ({
    prospectId,
    date,
    time,
    content,
    channel,
  }) => {
    const normalizedContent = String(content || "").trim();

    if (!prospectId) {
      toast.error("Sélectionnez un prospect ou un client.");
      return false;
    }

    if (!normalizedContent) {
      toast.error("Renseignez la relance.");
      return false;
    }

    const followupAt = new Date(`${date}T${time}:00`);
    if (Number.isNaN(followupAt.getTime())) {
      toast.error("Date ou heure de relance invalide.");
      return false;
    }

    setNewActionSaving(true);

    try {
      await prospectsApi.addNote(prospectId, {
        activity_type: "followup",
        activity_date: new Date().toISOString(),
        outcome: null,
        content: normalizedContent,
        next_action_type: "followup",
        next_action_channel:
          channel || "other",
        next_action: normalizedContent,
        next_action_at: followupAt.toISOString(),
      });

      await loadAgenda();
      toast.success("Relance ajoutée");
      return true;
    } catch (requestError) {
      toast.error(
        formatApiError(requestError?.response?.data?.detail) ||
          "Création de la relance impossible"
      );
      return false;
    } finally {
      setNewActionSaving(false);
    }
  };


  const openNewMeeting = async (
    requestedProspectId = ""
  ) => {
    setMeetingContactsLoading(true);

    try {
      const response =
        await prospectsApi.list();

      const contacts =
        safeArray(response)
          .filter(
            (contact) =>
              contact?.id
          )
          .sort(
            (
              first,
              second
            ) =>
              prospectTitle(first)
                .localeCompare(
                  prospectTitle(second),
                  "fr-FR"
                )
          );

      if (!contacts.length) {
        toast.error(
          "Aucun prospect ou client disponible."
        );
        return;
      }

      setMeetingContacts(
        contacts
      );

      const start =
        new Date(
          Date.now() +
          60 * 60 * 1000
        );

      start.setMinutes(
        Math.ceil(
          start.getMinutes() /
          15
        ) * 15,
        0,
        0
      );

      const selectedContact =
        contacts.find(
          (contact) =>
            contact.id ===
            requestedProspectId
        ) ||
        contacts[0];

      setMeetingEditor({
        source: "new",
        action: null,
        prospectId:
          selectedContact.id,
        date: dateKey(start),
        start:
          start
            .toTimeString()
            .slice(0, 5),
        durationMinutes: "30",
        meetingType: "online",
        location: "",
        subject:
          buildMeetingDefaultSubject(
            selectedContact,
            user || {}
          ),
        description:
          DEFAULT_MEETING_DESCRIPTION,
      });
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            ?.response
            ?.data
            ?.detail
        ) ||
        "Chargement des contacts impossible"
      );
    } finally {
      setMeetingContactsLoading(
        false
      );
    }
  };


  // ACTA_COMMERCE_AGENDA_MEETING_EDIT_V1
  const openMeetingEditor = (
    action,
    {
      forceEdit = false,
    } = {}
  ) => {
    if (
      !forceEdit &&
      actionIsMeeting(action)
    ) {
      openMeetingOutcome(action);
      return true;
    }

    const dedicatedAppointment =
      action?.agendaSource ===
        "prospect_appointment" &&
      action?.nextActionType ===
        "meeting" &&
      (
        action?.appointmentId ||
        action?.id
      );

    const legacyProspectMeeting =
      [
        "prospect",
        "prospect_meeting",
      ].includes(
        action?.agendaSource
      ) &&
      action?.nextActionType ===
        "meeting" &&
      action?.nextActionNoteId;

    const shareMeeting =
      action?.agendaSource ===
        "share_meeting" &&
      action?.meetingId &&
      action?.shareId;

    if (
      !dedicatedAppointment &&
      !legacyProspectMeeting &&
      !shareMeeting
    ) {
      return false;
    }

    if (shareMeeting) {
      const slot =
        action.selectedSlot ||
        {};

      setMeetingEditor({
        source: "share_meeting",
        action,
        startEditing:
          forceEdit,
        date: slot.date || "",
        start: slot.start || "",
        durationMinutes:
          String(
            slot.durationMinutes ||
            30
          ),
        meetingType:
          slot.locationType ||
          "online",
        location:
          slot.locationType ===
            "physical"
            ? slot.location || ""
            : "",
        subject:
          action.nextAction ||
          "Rendez-vous confirmé",
        description:
          action.agendaDescription ||
          "",
      });

      return true;
    }

    const parsed =
      dedicatedAppointment
        ? {}
        : parseMeetingContent(
            action.nextActionNoteContent
          );

    setMeetingEditor({
      source:
        dedicatedAppointment
          ? "prospect_appointment"
          : "prospect",
      action,
      startEditing:
        forceEdit,
      date:
        dateKey(
          action.nextActionAt
        ),
      start:
        formatTime(
          action.nextActionAt
        ),
      durationMinutes:
        String(
          action.durationMinutes ||
          parsed.durationMinutes ||
          30
        ),
      meetingType:
        action.meetingType ||
        parsed.meetingType ||
        "online",
      location:
        action.location ||
        parsed.location ||
        buildProspectMeetingAddress(
          action
        ),
      subject:
        action.appointmentSubject ||
        parsed.subject ||
        action.nextAction ||
        "Rendez-vous",
      description:
        action.agendaDescription ||
        parsed.description ||
        "",
    });

    return true;
  };



  // ACTA_AGENDA_FOLLOWUP_WORKFLOW_V1
  const openFollowupEditor = (action) => {
    const editableAction =
      !actionIsMeeting(action) &&
      [
        "prospect",
        "prospect_followup_history",
      ].includes(
        action?.agendaSource
      ) &&
      action?.nextActionNoteId;

    if (!editableAction) {
      return false;
    }

    setFollowupEditor({
      action,
      date: dateKey(action.nextActionAt),
      start: formatTime(action.nextActionAt),
      content: String(action.nextAction || "").trim(),
      channel:
        action.nextActionChannel ||
        "other",
      genericAction:
        !actionIsFollowup(action),
    });

    return true;
  };


  const openAgendaAction = (action) => {
    if (actionIsMeeting(action)) {
      return openMeetingEditor(action);
    }

    return openFollowupEditor(action);
  };


  const saveFollowupEditor = async (event) => {
    event.preventDefault();

    if (!followupEditor?.action?.nextActionNoteId) {
      return;
    }

    const content = String(
      followupEditor.content || ""
    ).trim();

    if (!content) {
      toast.error("Renseignez la relance.");
      return;
    }

    if (content.length > 500) {
      toast.error("La relance est limitée à 500 caractères.");
      return;
    }

    const followupAt = new Date(
      `${followupEditor.date}T${followupEditor.start}:00`
    );

    if (Number.isNaN(followupAt.getTime())) {
      toast.error("Date ou heure de relance invalide.");
      return;
    }

    setFollowupSaving(true);

    try {
      await prospectsApi.updateNote(
        followupEditor.action.nextActionNoteId,
        {
          activity_type: "followup",
          outcome: null,
          content,
          next_action_type: "followup",
          next_action_channel:
            followupEditor.channel ||
            "other",
          next_action: content,
          next_action_at: followupAt.toISOString(),
        }
      );

      await loadAgenda();
      setFollowupEditor(null);
      toast.success("Relance modifiée");
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError?.response?.data?.detail
        ) || "Modification de la relance impossible"
      );
    } finally {
      setFollowupSaving(false);
    }
  };


  const completeFollowupEditor = async () => {
    const noteId =
      followupEditor?.action?.nextActionNoteId;

    if (!noteId) {
      return;
    }

    setFollowupSaving(true);

    try {
      if (followupEditor?.genericAction) {
        await prospectsApi.completeAction(
          noteId
        );
      } else {
        await prospectsApi.completeFollowup(
          noteId
        );
      }

      await loadAgenda();

      setFollowupEditor(
        (current) =>
          current
            ? {
                ...current,
                action: {
                  ...current.action,
                  ...(
                    current.genericAction
                      ? {
                          actionStatus:
                            "completed",
                        }
                      : {
                          followupStatus:
                            "completed",
                        }
                  ),
                  nextActionStatus:
                    "completed",
                },
              }
            : current
      );

      toast.success(
        "Action catégorisée : action faite"
      );
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError?.response?.data?.detail
        ) || "Catégorisation de l’action impossible"
      );
    } finally {
      setFollowupSaving(false);
    }
  };


  const deleteFollowupEditor = async () => {
    if (
      !followupEditor?.action?.nextActionNoteId ||
      !window.confirm("Supprimer définitivement cette relance ?")
    ) {
      return;
    }

    setFollowupSaving(true);

    try {
      await prospectsApi.removeNote(
        followupEditor.action.nextActionNoteId
      );
      await loadAgenda();
      setFollowupEditor(null);
      toast.success("Relance supprimée");
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError?.response?.data?.detail
        ) || "Suppression de la relance impossible"
      );
    } finally {
      setFollowupSaving(false);
    }
  };


  const saveMeetingEditor = async (event) => {
    event.preventDefault();
    const meetingAt =
      new Date(
        `${meetingEditor.date}T${meetingEditor.start}:00`
      );

    if (
      Number.isNaN(
        meetingAt.getTime()
      ) ||
      meetingAt.getTime() <
        Date.now() +
        5 * 60 * 1000
    ) {
      toast.error(
        "Le rendez-vous doit être prévu au moins 5 minutes dans le futur."
      );
      return;
    }

    const subject =
      String(
        meetingEditor.subject ||
        ""
      ).trim();

    if (!subject) {
      toast.error(
        "Renseignez l’objet du rendez-vous."
      );
      return;
    }

    if (
      meetingEditor.source ===
        "new" &&
      !meetingEditor.prospectId
    ) {
      toast.error(
        "Sélectionnez un prospect ou un client."
      );
      return;
    }

    const typeLabel =
      meetingEditor.meetingType ===
        "physical"
        ? "Physique"
        : "En ligne";

    const legacyContent = [
      subject,
      `Type : ${typeLabel}`,
      `Durée : ${meetingEditor.durationMinutes} minutes`,
      meetingEditor.meetingType ===
        "physical"
        ? (
            `Adresse : ${String(
              meetingEditor.location ||
              ""
            ).trim()}`
          )
        : "",
      meetingEditor.description
        ? (
            `Description : ${String(
              meetingEditor.description
            ).trim()}`
          )
        : "",
    ]
      .filter(Boolean)
      .join("\n");

    const iso =
      meetingAt.toISOString();

    const appointmentPayload = {
      start_at: iso,
      duration_minutes:
        Number(
          meetingEditor.durationMinutes ||
          30
        ),
      meeting_type:
        meetingEditor.meetingType ||
        "online",
      subject,
      description:
        String(
          meetingEditor.description ||
          ""
        ).trim(),
      location:
        meetingEditor.meetingType ===
          "physical"
          ? String(
              meetingEditor.location ||
              ""
            ).trim()
          : "",
    };

    setMeetingSaving(true);

    try {
      if (
        meetingEditor.source ===
        "new"
      ) {
        const savedAppointment =
          await appointmentsApi.create(
            meetingEditor.prospectId,
            appointmentPayload
          );

        const selectedContact =
          meetingContacts.find(
            (contact) =>
              contact?.id ===
              meetingEditor.prospectId
          ) || {};

        setMeetingEditor(
          (current) =>
            current
              ? {
                  ...current,
                  source:
                    "new_saved",
                  savedAppointmentId:
                    savedAppointment?.id ||
                    null,
                  action: {
                    ...selectedContact,
                    agendaSource:
                      "prospect_appointment",
                    prospectId:
                      meetingEditor
                        .prospectId,
                    appointmentId:
                      savedAppointment?.id ||
                      null,
                    nextActionType:
                      "meeting",
                    nextAction:
                      subject,
                    nextActionAt:
                      savedAppointment
                        ?.startAt ||
                      iso,
                    durationMinutes:
                      savedAppointment
                        ?.durationMinutes ||
                      appointmentPayload
                        .duration_minutes,
                    meetingType:
                      savedAppointment
                        ?.meetingType ||
                      appointmentPayload
                        .meeting_type,
                    location:
                      savedAppointment
                        ?.location ||
                      appointmentPayload
                        .location,
                    agendaDescription:
                      savedAppointment
                        ?.description ||
                      appointmentPayload
                        .description,
                  },
                }
              : current
        );

      } else if (
        meetingEditor.source ===
        "new_saved"
      ) {
        await appointmentsApi.update(
          meetingEditor
            .savedAppointmentId,
          appointmentPayload
        );

      } else if (
        meetingEditor.source ===
        "share_meeting"
      ) {
        await sharesApi
          .updateConfirmedExchangeMeeting(
            meetingEditor.action.shareId,
            meetingEditor.action.meetingId,
            {
              subject,
              description:
                String(
                  meetingEditor.description ||
                  ""
                ).trim(),
              slot: {
                date:
                  meetingEditor.date,
                start:
                  meetingEditor.start,
                durationMinutes:
                  Number(
                    meetingEditor.durationMinutes ||
                    30
                  ),
                locationType:
                  meetingEditor.meetingType,
                location:
                  meetingEditor.meetingType ===
                    "online"
                    ? "En ligne"
                    : String(
                        meetingEditor.location ||
                        ""
                      ).trim(),
              },
            }
          );

      } else if (
        meetingEditor.source ===
        "prospect_appointment"
      ) {
        const appointmentId =
          meetingEditor
            .action
            ?.appointmentId ||
          meetingEditor
            .action
            ?.id;

        if (!appointmentId) {
          throw new Error(
            "Rendez-vous introuvable"
          );
        }

        await appointmentsApi.update(
          appointmentId,
          appointmentPayload
        );

      } else {
        // Compatibilité historique uniquement.
        await prospectsApi.updateNote(
          meetingEditor
            .action
            .nextActionNoteId,
          {
            activity_type:
              "meeting",
            activity_date:
              iso,
            outcome: null,
            content:
              legacyContent,
            next_action_type:
              "meeting",
            next_action:
              subject,
            next_action_at:
              iso,
          }
        );
      }

      await loadAgenda();

      if (
        meetingEditor.source ===
        "new"
      ) {
        toast.success(
          "Rendez-vous ajouté"
        );
        return;
      }

      if (
        meetingEditor.source ===
        "new_saved"
      ) {
        setMeetingEditor(
          (current) =>
            current
              ? {
                  ...current,
                  subject,
                  startEditing: false,
                }
              : current
        );

        toast.success(
          "Rendez-vous modifié"
        );
        return;
      }

      setMeetingEditor(null);

      toast.success(
        "Rendez-vous modifié"
      );

    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            ?.response
            ?.data
            ?.detail
        ) ||
        (
          meetingEditor.source ===
            "new"
            ? "Création du rendez-vous impossible"
            : "Modification du rendez-vous impossible"
        )
      );

    } finally {
      setMeetingSaving(false);
    }
  };

  const requestDeleteMeeting = (
    action
  ) => {
    if (!action) {
      return;
    }

    setMeetingDeleteTarget(
      action
    );
  };


  const confirmDeleteMeeting = async () => {
    const action =
      meetingDeleteTarget;

    if (!action) {
      return;
    }

    setMeetingSaving(true);
    setMeetingOutcomeSaving(true);

    try {
      const source =
        meetingSource(action);

      if (
        source ===
        "share_meeting"
      ) {
        await sharesApi
          .deleteExchangeMeeting(
            action.shareId,
            action.meetingId
          );

      } else if (
        source ===
        "prospect_appointment"
      ) {
        const appointmentId =
          action.appointmentId ||
          action.id;

        if (!appointmentId) {
          throw new Error(
            "Rendez-vous introuvable"
          );
        }

        await appointmentsApi.remove(
          appointmentId
        );

      } else {
        // Compatibilité historique.
        await prospectsApi
          .removeNote(
            action.nextActionNoteId
          );
      }

      await loadAgenda();

      setMeetingDeleteTarget(
        null
      );
      setMeetingEditor(null);
      setMeetingOutcomeEditor(
        null
      );
      setMeetingNoShowEditor(
        null
      );

      toast.success(
        "Rendez-vous supprimé"
      );
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            ?.response
            ?.data
            ?.detail
        ) ||
        "Suppression du rendez-vous impossible"
      );
    } finally {
      setMeetingSaving(false);
      setMeetingOutcomeSaving(
        false
      );
    }
  };


  const deleteMeetingEditor = () => {
    requestDeleteMeeting(
      meetingEditor?.action
    );
  };


  // ACTA_AGENT_AGENDA_WORKFLOW_BRIDGE_V3
  useEffect(() => {
    if (
      loading ||
      !requestedAgentMeetingKey ||
      !requestedAgentMeetingAction
    ) {
      if (
        !requestedAgentMeetingKey
      ) {
        agentMeetingBridgeRef.current =
          "";
      }

      return;
    }

    const fingerprint =
      `${requestedAgentMeetingAction}:`
      + `${requestedAgentMeetingKey}`;

    if (
      agentMeetingBridgeRef.current ===
        fingerprint
    ) {
      return;
    }

    agentMeetingBridgeRef.current =
      fingerprint;

    const consumeAgentParams =
      () => {
        const nextParams =
          new URLSearchParams(
            searchParams
          );

        [
          "agentMeetingKey",
          "agentMeetingAction",
          "agentMeetingOutcome",
          "agentMeetingDate",
          "agentMeetingStart",
          "agentMeetingDurationMinutes",
          "agentMeetingMeetingType",
          "agentMeetingSubject",
        ].forEach(
          (key) =>
            nextParams.delete(
              key
            )
        );

        setSearchParams(
          nextParams,
          {
            replace: true,
          }
        );
      };

    const action =
      prospects.find(
        (item) =>
          agendaAgentMeetingKey(
            item
          ) ===
          requestedAgentMeetingKey
      );

    if (
      !action ||
      !actionIsMeeting(
        action
      )
    ) {
      toast.error(
        "Rendez-vous introuvable ou inaccessible."
      );

      consumeAgentParams();

      return;
    }

    if (
      requestedAgentMeetingAction ===
        "outcome"
    ) {
      if (
        !meetingIsPast(
          action
        ) &&
        !meetingOutcomeCode(
          action
        )
      ) {
        toast.error(
          "Ce rendez-vous n’est pas encore terminé."
        );

        consumeAgentParams();

        return;
      }

      openMeetingOutcome(
        action
      );

      if (
        requestedAgentMeetingOutcome &&
        MEETING_OUTCOMES[
          requestedAgentMeetingOutcome
        ]
      ) {
        setMeetingOutcomeEditor(
          (current) =>
            current
              ? {
                  ...current,
                  resultCode:
                    requestedAgentMeetingOutcome,
                }
              : current
        );
      }

      consumeAgentParams();

      return;
    }

    if (
      requestedAgentMeetingAction ===
        "edit"
    ) {
      if (
        meetingIsPast(
          action
        ) ||
        meetingOutcomeCode(
          action
        )
      ) {
        toast.error(
          "Un rendez-vous terminé ne peut plus être replanifié depuis l’éditeur."
        );

        consumeAgentParams();

        return;
      }

      const opened =
        openMeetingEditor(
          action,
          { forceEdit: true }
        );

      if (!opened) {
        toast.error(
          "Ce rendez-vous ne peut pas être modifié depuis l’agenda."
        );

        consumeAgentParams();

        return;
      }

      setMeetingEditor(
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            ...(
              requestedAgentMeetingDate
                ? {
                    date:
                      requestedAgentMeetingDate,
                  }
                : {}
            ),
            ...(
              requestedAgentMeetingStart
                ? {
                    start:
                      requestedAgentMeetingStart,
                  }
                : {}
            ),
            ...(
              requestedAgentMeetingDuration
                ? {
                    durationMinutes:
                      requestedAgentMeetingDuration,
                  }
                : {}
            ),
            ...(
              [
                "online",
                "physical",
              ].includes(
                requestedAgentMeetingType
              )
                ? {
                    meetingType:
                      requestedAgentMeetingType,
                  }
                : {}
            ),
            ...(
              requestedAgentMeetingSubject
                ? {
                    subject:
                      requestedAgentMeetingSubject,
                  }
                : {}
            ),
          };
        }
      );

      consumeAgentParams();

      return;
    }

    consumeAgentParams();
  }, [
    loading,
    prospects,
    requestedAgentMeetingKey,
    requestedAgentMeetingAction,
    requestedAgentMeetingOutcome,
    requestedAgentMeetingDate,
    requestedAgentMeetingStart,
    requestedAgentMeetingDuration,
    requestedAgentMeetingType,
    requestedAgentMeetingSubject,
  ]);


  if (loading) {
    return (
      <div
        className="p-4 sm:p-6 lg:p-8"
        data-testid="commerce-agenda-loading"
      >
        <div className="label-eyebrow lirah-pulse">
          Chargement de l’agenda commercial
        </div>
      </div>
    );
  }


  if (error) {
    return (
      <div
        className="max-w-3xl p-4 sm:p-6 lg:p-8 acta-standard-page acta-commerce-page-standard"
        data-testid="commerce-agenda-error"
      >
        <div className="lirah-card p-8">
          <h1 className="font-heading text-xl font-black">Agenda</h1>

          <p className="mt-2 text-sm text-[var(--do-muted)]">
            {error}
          </p>

          <button
            type="button"
            onClick={loadAgenda}
            className="mt-4 rounded-lg bg-[var(--do-blue)] px-4 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)]"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }


  return (
    <ActaStandardPage testId="commerce-agenda-page">
      <div
        className="hidden md:block"
        data-testid="commerce-agenda-metrics-desktop"
      >
        <section
          className="pipeline-final-metrics"
          data-testid="commerce-agenda-metrics"
        >
          <CommerceMetricCard
            label="Aujourd’hui"
            value={todayActions.length}
            detail="Actions prévues"
            icon={CalendarCheck}
            tone="blue"
            testId="agenda-metric-today"
          />

          <CommerceMetricCard
            label="Cette semaine"
            value={weekActionCount}
            detail="Actions planifiées"
            icon={Clock}
            tone="green"
            testId="agenda-metric-week"
          />

          <CommerceMetricCard
            label="En retard"
            value={overdueActions.length}
            detail="Actions dont la date est dépassée"
            icon={WarningCircle}
            tone="red"
            testId="agenda-metric-overdue"
          />

          <CommerceMetricCard
            label="Rendez-vous"
            value={meetingCount}
            detail="Dans les résultats filtrés"
            icon={Handshake}
            tone="amber"
            testId="agenda-metric-meetings"
          />
        </section>
      </div>

      <div
        className="lirah-card p-2.5 sm:p-3"
        data-testid="commerce-agenda-toolbar"
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2 lg:gap-3">

          <label className="relative min-w-0 basis-full sm:flex-[1_1_16rem]">
            <span className="sr-only">
              Rechercher dans l’agenda
            </span>

            <MagnifyingGlass
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
            />

            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Recherche prospects, sociétés, actions..."
              className="h-10 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] pl-9 pr-3 text-base outline-none focus:border-[var(--do-accent)] sm:text-xs"
              data-testid="commerce-agenda-search"
            />
          </label>

          {canFilterCommercials ? (
            <div
              className="min-w-0 flex-1 sm:flex-[1_1_11rem] sm:max-w-[220px]"
              data-testid="agenda-admin-owner-filter"
            >
              <CommercialScopeFilter
                value={commercialFilter}
                onChange={setCommercialFilter}
                options={agendaCommercialOptions}
                ariaLabel="Filtrer l’agenda par commercial"
                testId="agenda-owner-user-filter"
              />
            </div>
          ) : null}

          <button
            type="button"
            onClick={openNewAction}
            disabled={meetingContactsLoading}
            aria-label="Nouvelle action"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-[var(--do-blue)] px-0 text-sm font-heading font-bold text-[var(--do-on-accent)] transition hover:opacity-90 disabled:opacity-50 lg:w-auto lg:px-4"
            data-testid="agenda-new-action"
          >
            <Plus
              size={16}
              weight="bold"
            />

            <span className="hidden lg:inline">
              Nouvelle action
            </span>
          </button>

          <button
            type="button"
            onClick={loadAgenda}
            aria-label="Actualiser l’agenda"
            className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] transition hover:bg-[var(--do-surface-soft)] lg:flex"
            data-testid="refresh-commerce-agenda"
          >
            <ArrowClockwise
              size={16}
              weight="bold"
            />
          </button>

        </div>
      </div>

      {overdueActions.length >
        0 && (
        <>
          <section
            className="mt-5 hidden overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] md:block"
            data-testid="commerce-agenda-overdue"
          >
            <div className="flex items-center justify-between gap-3 border-b border-[var(--do-border)] px-4 py-3">
              <div>
                <div className="text-xs font-heading font-bold uppercase tracking-[0.12em] text-[var(--do-accent)]">
                  Priorité
                </div>

                <h2 className="mt-0.5 font-heading text-sm font-black text-[var(--do-ink)]">
                  Actions à traiter
                </h2>
              </div>

              <span className="rounded-full bg-[var(--do-accent-soft)] px-2.5 py-1 text-xs font-heading font-bold text-[var(--do-accent)]">
                {overdueActions.length}
              </span>
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(14rem,100%),1fr))] gap-3 p-3">
              {overdueActions
                .map(
                  (prospect) => (
                    <ActionCard
                      key={prospect.id}
                      prospect={prospect}
                      compact
                      overdue
                      onOpen={openAgendaAction}
                    />
                  )
                )}
            </div>
          </section>

          <section
            className="mt-3 md:hidden"
            data-testid="commerce-agenda-overdue-mobile"
          >
            <div className="flex items-end justify-between gap-3 border-b border-[var(--do-border)] pb-2">
              <div className="min-w-0">
                <div className="text-[11px] font-heading font-bold uppercase tracking-[0.12em] text-[var(--do-accent)]">
                  Priorité
                </div>

                <h2 className="mt-0.5 font-heading text-sm font-black text-[var(--do-ink)]">
                  Actions à traiter
                </h2>
              </div>

              <span className="shrink-0 text-xs font-heading font-bold text-[var(--do-accent)]">
                {overdueActions.length}
              </span>
            </div>

            <MobileActionList
              items={overdueActions}
              overdue
              onOpen={openAgendaAction}
              testId="agenda-mobile-overdue-list"
            />
          </section>
        </>
      )}

      <div className="mt-3 flex flex-col gap-3 sm:mt-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="hidden min-w-0 md:block">
          <div className="label-eyebrow">
            Semaine
          </div>

          <h2 className="mt-1 font-heading text-base font-black text-[var(--do-ink)]">
            Du{" "}
            {formatShortDate(
              weekStart
            )}{" "}
            au{" "}
            {formatShortDate(
              addDays(
                weekStart,
                6
              )
            )}
          </h2>
        </div>

        <div className="grid w-full grid-cols-[2.25rem_minmax(0,1fr)_2.25rem] items-center gap-2 sm:flex sm:w-auto">
          <button
            type="button"
            onClick={() =>
              setWeekStart(
                addDays(
                  weekStart,
                  -7
                )
              )
            }
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)] transition hover:bg-[var(--do-surface-soft)]"
            aria-label="Semaine précédente"
            data-testid="agenda-previous-week"
          >
            <CaretLeft
              size={16}
              weight="bold"
            />
          </button>

          <button
            type="button"
            onClick={goToCurrentWeek}
            className="h-9 min-w-0 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2 text-[11px] font-heading font-bold text-[var(--do-ink)] transition hover:bg-[var(--do-surface-soft)] sm:px-4 sm:text-xs"
            data-testid="agenda-current-week"
          >
            <span
              className="md:hidden"
              data-testid="agenda-mobile-week-range"
            >
              {formatNumericDate(
                weekStart
              )}{" "}
              -{" "}
              {formatNumericDate(
                addDays(
                  weekStart,
                  6
                )
              )}
            </span>
            <span className="hidden md:inline">
              Aujourd’hui
            </span>
          </button>

          <button
            type="button"
            onClick={() =>
              setWeekStart(
                addDays(
                  weekStart,
                  7
                )
              )
            }
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)] transition hover:bg-[var(--do-surface-soft)]"
            aria-label="Semaine suivante"
            data-testid="agenda-next-week"
          >
            <CaretRight
              size={16}
              weight="bold"
            />
          </button>
        </div>
      </div>

      <section
        className="mt-3 hidden min-w-0 overflow-x-auto pb-3 md:block"
        data-testid="commerce-agenda-week"
      >
        <div className="grid min-w-[1260px] grid-cols-7 gap-3">
          {weekDays.map(
            (day) => {
              const key =
                dateKey(day);

              const items =
                actionsByDay[key] ||
                [];

              const isToday =
                key === todayKey;

              return (
                <article
                  key={key}
                  className="lirah-card min-h-[460px] overflow-hidden"
                  data-testid={`agenda-day-${key}`}
                >
                  <header
                    className={`border-b px-3 py-3 ${
                      isToday
                        ? "border-[var(--do-border)] bg-[var(--do-accent-soft)]"
                        : "border-[var(--do-border)]"
                    }`}
                  >
                    <div
                      className={`text-xs font-heading font-bold uppercase tracking-[0.12em] ${
                        isToday
                          ? "text-[var(--do-accent)]"
                          : "text-[var(--do-muted)]"
                      }`}
                    >
                      {day.toLocaleDateString(
                        "fr-FR",
                        {
                          weekday:
                            "long",
                        }
                      )}
                    </div>

                    <div className="mt-1 flex items-end justify-between gap-2">
                      <span className="font-heading text-xl font-black text-[var(--do-ink)]">
                        {day.getDate()}
                      </span>

                      <span className="rounded-full bg-[var(--do-surface-soft)] px-2 py-0.5 text-xs font-heading font-bold text-[var(--do-muted)]">
                        {items.length}
                      </span>
                    </div>
                  </header>

                  <div className="space-y-2.5 bg-[var(--do-surface-soft)] p-2.5">
                    {!items.length ? (
                      <div className="flex min-h-24 items-center justify-center rounded-xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-center text-xs text-[var(--do-muted)]">
                        Aucune action prévue.
                      </div>
                    ) : (
                      items.map(
                        (prospect) => (
                          <ActionCard
                            key={
                              prospect.id
                            }
                            prospect={
                              prospect
                            }
                            compact
                            overdue={actionIsOverdue(
                              prospect
                            )}
                            onOpen={openAgendaAction}
                          />
                        )
                      )
                    )}
                  </div>
                </article>
              );
            }
          )}
        </div>
      </section>

      <section
        className="mt-3 space-y-3 md:hidden"
        data-testid="commerce-agenda-mobile"
      >
        {weekDays.map(
          (day) => {
            const key =
              dateKey(day);

            const items =
              actionsByDay[key] ||
              [];

            if (
              !items.length
            ) {
              return null;
            }

            return (
              <article
                key={key}
                className="min-w-0"
                data-testid={`agenda-mobile-day-${key}`}
              >
                <header className="min-w-0 rounded-lg bg-[var(--do-accent-soft)] px-3 py-2">
                  <div className="min-w-0 truncate font-heading text-sm font-black text-[var(--do-accent)]">
                    {formatFullDate(
                      day
                    )}
                  </div>
                </header>

                <MobileActionList
                  items={items}
                  onOpen={openAgendaAction}
                  testId={`agenda-mobile-day-list-${key}`}
                />
              </article>
            );
          }
        )}

        {!weekActionCount && (
          <div className="border-y border-[var(--do-border)] py-8 text-center text-xs text-[var(--do-muted)]">
            Aucune action prévue cette semaine.
          </div>
        )}
      </section>

      <MeetingOutcomeDialog
        editor={meetingOutcomeEditor}
        saving={meetingOutcomeSaving}
        contactName={
          meetingOutcomeEditor
            ? prospectTitle(
                meetingOutcomeEditor
                  .action
              )
            : ""
        }
        meetingLabel={
          meetingOutcomeEditor
            ? (
                `${formatFullDate(
                  meetingOutcomeEditor
                    .action
                    .nextActionAt
                )} à ${formatTime(
                  meetingOutcomeEditor
                    .action
                    .nextActionAt
                )}`
              )
            : ""
        }
        reportUrl={
          meetingOutcomeEditor
            ? meetingReportUrl(
                meetingOutcomeEditor
                  .action
              )
            : ""
        }
        calendarLinks={
          meetingOutcomeEditor
            ? calendarLinksForAction(
                meetingOutcomeEditor
                  .action
              )
            : null
        }
        onOutcome={
          saveMeetingOutcome
        }
        onNotDone={
          openMeetingNoShow
        }
        onReschedule={() => {
          const action =
            meetingOutcomeEditor
              ?.action;

          if (!action) {
            return;
          }

          setMeetingOutcomeEditor(
            null
          );

          openMeetingEditor(
            action,
            {
              forceEdit: true,
            }
          );
        }}
        onDelete={() =>
          requestDeleteMeeting(
            meetingOutcomeEditor
              ?.action
          )
        }
        onClose={() =>
          setMeetingOutcomeEditor(
            null
          )
        }
      />

      <CommerceAgendaMeetingNoShowDialog
        open={
          Boolean(
            meetingNoShowEditor
          )
        }
        saving={
          meetingOutcomeSaving
        }
        contactName={
          meetingNoShowEditor
            ?.action
            ? prospectTitle(
                meetingNoShowEditor
                  .action
              )
            : ""
        }
        onPlanNewMeeting={(note) =>
          saveMeetingNoShow({
            note,
            replanify: true,
          })
        }
        onDoNotPlan={(note) =>
          saveMeetingNoShow({
            note,
          })
        }
        onSkip={() =>
          saveMeetingNoShow({
            skip: true,
          })
        }
        onClose={() =>
          setMeetingNoShowEditor(
            null
          )
        }
      />

      <ActaConfirmDialog
        open={
          Boolean(
            meetingDeleteTarget
          )
        }
        eyebrow="Agenda commercial"
        title="Supprimer le rendez-vous ?"
        description="Cette action supprimera définitivement le rendez-vous."
        confirmLabel="Supprimer le rendez-vous"
        danger
        saving={
          meetingSaving ||
          meetingOutcomeSaving
        }
        onConfirm={
          confirmDeleteMeeting
        }
        onClose={() =>
          setMeetingDeleteTarget(
            null
          )
        }
      />

      <CommerceAgendaFollowupDialog
        editor={followupEditor}
        saving={followupSaving}
        contactName={
          followupEditor?.action
            ? prospectTitle(followupEditor.action)
            : ""
        }
        contactUrl={
          followupEditor?.action
            ? `/app/commerce/prospects/${encodeURIComponent(
                actionProspectId(followupEditor.action)
              )}`
            : ""
        }
        genericAction={Boolean(followupEditor?.genericAction)}
        actionTypeLabel={
          followupEditor?.action
            ? actionMeta(
                followupEditor.action.nextActionType
              ).label
            : ""
        }
        onChange={updateFollowupEditor}
        onSubmit={saveFollowupEditor}
        onComplete={completeFollowupEditor}
        onDelete={deleteFollowupEditor}
        onClose={() => setFollowupEditor(null)}
      />


      <CommerceAgendaNewActionDialog
        open={newActionOpen}
        contacts={meetingContacts}
        loading={meetingContactsLoading}
        saving={newActionSaving}
        onMeeting={(prospectId) => openNewMeeting(prospectId)}
        onCreateFollowup={createFollowup}
        initialMode={newActionInitialMode}
        initialProspectId={newActionInitialProspectId}
        onClose={() => setNewActionOpen(false)}
      />

      <MeetingEditorDialog
        editor={meetingEditor}
        saving={meetingSaving}
        contacts={meetingContacts}
        contactName={
          meetingEditor?.action
            ? prospectTitle(
                meetingEditor.action
              )
            : ""
        }
        calendarLinks={
          calendarLinksForEditor
        }
        onChange={
          updateMeetingEditor
        }
        onSubmit={
          saveMeetingEditor
        }
        onDelete={
          deleteMeetingEditor
        }
        onClose={() =>
          setMeetingEditor(null)
        }
      />
    </ActaStandardPage>
  );
}
